import React, {useState} from 'react';
import {Box, Typography, Grid, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, GuidanceBanner, MarkerBar, BenchmarkLegend, BandChip, readinessBandColor} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { READINESS_BAND_COLORS } from '../../../utils/constants';
import {isSuppressedItem, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';

import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortBars} from '../../DashboardCommon/barSort';
// A figure as a whole number (the rounding every dashboard uses); null when it is not sent (or under the n floor).
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null);

const ItemRow = ({itemLabel, label, score, band, national, global, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.2, pl: 4}}>
        <Box sx={{width: {xs: 160, md: 220}, flexShrink: 0}}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>{itemLabel}</Typography>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (
            <>
                <MarkerBar score={score} color={readinessBandColor(band)} national={national} global={global} height={12}/>
                <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY, width: 30, textAlign: 'right', flexShrink: 0}}>{score === null ? '—' : score}</Typography>
                <Box sx={{flexShrink: 0}}>{band && <BandChip label={band} color={readinessBandColor(band)}/>}</Box>
            </>
        )}
    </Box>
);

const ListCard = ({title, items, color}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%'}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((it) => (
                    <Box component="li" key={it} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>{it}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const READINESS_ITEM_PROFILE_SECTION_CODE = 'S_5b';
const CARD_SECTION = READINESS_ITEM_PROFILE_SECTION_CODE;

const ItemProfileTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    // Get the S_5b section
    const s6Section = dashboardData?.cards?.sections?.find(s => s.section_code === READINESS_ITEM_PROFILE_SECTION_CODE);
    if (!s6Section) {
        return <NoDataAvailable />;
    }
    const s6Cards = s6Section?.cards || [];

    // Extract dynamic TitleCard if available
    const titleCard = s6Cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || "Readiness Item Profile";
    const headerSubtitle = titleCard?.footer || "Where is the school performing well, and where does it need to improve?";

    // Extract dynamic BarChartBMCard (competencies item scores)
    const barChartCard = s6Cards.find(c => c.card_type === 'BarChartBMCard');
    const items = barChartCard?.items || [];

    // Transform items to UI structure
    const dynamicItems = items.map((item, index) => {
        const natVal = item.benchmark_values?.find(b => b.name?.includes('National'))?.value;
        const globVal = item.benchmark_values?.find(b => b.name?.includes('Global'))?.value;

        const areaNum = item.parent_sublabel ? (item.parent_sublabel.match(/\d+/)?.[0] || '1') : '1';

        return {
            code: item.code || item.sublabel || `COSI ${String(index + 1).padStart(2, '0')}`,
            itemLabel: '',                       // numbered below, 1…N straight through the areas (QA 9 Oct)
            areaTitle: `Readiness Area ${areaNum}`,
            parentLabel: item.parent_label || 'Readiness Area',
            label: item.label,
            suppressed: isSuppressedItem(item),
            score: isSuppressedItem(item) ? null : num(item.value),      // null: not sent, or under the n floor
            band: isSuppressedItem(item) ? null : (item.band || null),
            national: num(natVal),
            global: num(globVal)
        };
    });

    // Group items by parentLabel
    const areasMap = new Map();
    dynamicItems.forEach((it) => {
        const groupKey = it.parentLabel;
        if (!areasMap.has(groupKey)) {
            areasMap.set(groupKey, {
                areaTitle: it.areaTitle,
                parentLabel: it.parentLabel,
                items: []
            });
        }
        areasMap.get(groupKey).items.push(it);
    });
    const groupedAreas = Array.from(areasMap.values());
    // Items are numbered in the order shown, continuing across areas ("Readiness Item 1 … 30"), whatever the card's own sublabel says.
    let itemNo = 0;
    groupedAreas.forEach((g) => g.items.forEach((it) => { itemNo += 1; it.itemLabel = `Readiness Item ${itemNo}`; }));
    const shownGroups = sortBars(groupedAreas, sortMode);

    // Compute strengths and needs attention dynamically — over the items that have a score
    // (items under the n floor are suppressed and take no part in the ranking).
    const scoredItems = dynamicItems.filter((it) => it.score !== null);
    const noneShown = dynamicItems.length > 0 && scoredItems.length === 0 ? [SUPPRESSED_COPY] : [];
    const sortedItems = [...scoredItems].sort((a, b) => b.score - a.score);
    const strengths = sortedItems.length ? sortedItems.slice(0, 3).map(it => `${it.label} (${Math.round(it.score)})`) : noneShown;
    const needs = sortedItems.length ? [...scoredItems].sort((a, b) => a.score - b.score).slice(0, 3).map(it => `${it.label} (${Math.round(it.score)})`) : noneShown;

    // Compute guidance text based on the lowest scoring item
    const lowestItem = sortedItems[sortedItems.length - 1];
    const guidanceText = lowestItem
        ? `"${lowestItem.label}" (${Math.round(lowestItem.score)}) is the lowest item. Start with one focused Area on it, then re-check the grade heatmap to target support where the gap is widest.`
        : "Start with one focused Area on it, then re-check the grade heatmap to target support where the gap is widest.";

    return (
        <Box>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                                {barChartCard?.title || "Readiness Item Scores"}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <BenchmarkLegend bandColors={READINESS_BAND_COLORS}/>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>
                        {shownGroups.map((areaGroup, idx) => (
                            <Box key={areaGroup.parentLabel || 'all'} sx={{mb: idx < shownGroups.length - 1 ? 3.5 : 0}}>
                                {(areaGroup.areaTitle || areaGroup.parentLabel) && <Box sx={{mb: 2}}>
                                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>
                                        {areaGroup.areaTitle}
                                    </Typography>
                                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>
                                        {areaGroup.parentLabel}
                                    </Typography>
                                </Box>}
                                {areaGroup.items.map((it) => (
                                    <ItemRow key={it.code} {...it} itemLabel={areaGroup.flattened && it.groupLabel ? `${it.itemLabel} · ${it.groupLabel}` : it.itemLabel}/>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ListCard title="Strengths" items={strengths} color="#1B6B55"/>
                    <Box sx={{mt: 2.5}}>
                        <ListCard title="Needs Attention" items={needs} color="#E04535"/>
                    </Box>
                    <GuidanceBanner label="Recommended action:" text={guidanceText}/>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ItemProfileTab;
